import './AddInputModal.scss';
import cn from 'classnames';
import { isBefore, startOfToday } from 'date-fns';
import { useCallback, useEffect, useMemo, useState, Dispatch, SetStateAction } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdOutlineSdCardAlert } from 'react-icons/md';
import { MediaTable } from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { Button } from '@Components/Button';
import { Checkbox, Radio } from '@Components/FormCheck';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { DateInputField } from '@Components/DatePicker/DateInputField/DateInputField';
import { CustomSegmentsData } from '@Pages/SegmentationMapping/segmentationMappingUtils';
import { RequiredSegmentIds } from './OfflineInputsUtils';

const MediaInputModalMode = {
  CREATE: 'create',
  EDIT: 'edit'
} as const;
type MediaInputModalModeType = (typeof MediaInputModalMode)[keyof typeof MediaInputModalMode];

const HasRedemptions = {
  TRUE: 'true',
  FALSE: 'false',
  UNSELECTED: 'unselected'
} as const;
type HasRedemptionsType = (typeof HasRedemptions)[keyof typeof HasRedemptions];

interface DmaRadioGroupProps {
  hasDma: boolean;
  setHasDma: Dispatch<SetStateAction<boolean>>;
}

const DmaRadioGroup = ({ hasDma, setHasDma }: DmaRadioGroupProps) => (
  <>
    <div className='section-title'>Breakdown by DMA?</div>
    <Radio label='True' checked={hasDma} id='hasDma-true' onChange={() => setHasDma(true)} />
    <Radio label='False' checked={!hasDma} id='hasDma-false' onChange={() => setHasDma(false)} />
  </>
);

interface CreateModeFieldsProps {
  isPaid: boolean;
  hasImpressions: boolean;
  setHasImpressions: Dispatch<SetStateAction<boolean>>;
  otherUnitChecked: boolean;
  setOtherUnitChecked: Dispatch<SetStateAction<boolean>>;
  otherUnitLabel: string;
  setOtherUnitLabel: Dispatch<SetStateAction<string>>;
  showSpendWarning: boolean;
  setShowSpendWarning: Dispatch<SetStateAction<boolean>>;
  hasRedemptions: HasRedemptionsType;
  setHasRedemptions: Dispatch<SetStateAction<HasRedemptionsType>>;
  hasDma: boolean;
  setHasDma: Dispatch<SetStateAction<boolean>>;
  startDateStr: string;
  setStartDateStr: Dispatch<SetStateAction<string>>;
}

const CreateModeFields = ({
  isPaid,
  hasImpressions,
  setHasImpressions,
  otherUnitChecked,
  setOtherUnitChecked,
  otherUnitLabel,
  setOtherUnitLabel,
  showSpendWarning,
  setShowSpendWarning,
  hasRedemptions,
  setHasRedemptions,
  hasDma,
  setHasDma,
  startDateStr,
  setStartDateStr
}: CreateModeFieldsProps) => (
  <>
    <div className='units-input'>
      <div className='section-title'>
        Units<span className='asterisk'>*</span>
        <div className='section-hint'>Select all that apply.</div>
      </div>
      <Checkbox
        label='Impressions'
        checked={hasImpressions}
        id='hasImpressions'
        onChange={() => setHasImpressions(!hasImpressions)}
      />
      <Checkbox
        label='Other'
        checked={otherUnitChecked}
        id='otherUnitChecked'
        onChange={() => setOtherUnitChecked(!otherUnitChecked)}
      />
      {otherUnitChecked && (
        <Form.Control
          placeholder='Ex: Delivered, Sends'
          value={otherUnitLabel}
          onChange={e => setOtherUnitLabel(e.target.value)}
        />
      )}
      {isPaid && (
        <>
          <Checkbox
            label='Spend (required)'
            checked
            readOnly
            id='spend'
            onClick={() => setShowSpendWarning(true)}
          />
          {showSpendWarning && (
            <div className='input-explanation warning-explanation'>
              <MdOutlineSdCardAlert size={18} />
              <p>
                If your channel doesn&apos;t have spend, please{' '}
                <b>switch to a non-paid media input.</b>
              </p>
            </div>
          )}
        </>
      )}
    </div>
    {isPaid && (
      <div className='has-redemptions-input'>
        <div className='section-title'>
          Redemptions<span className='asterisk'>*</span>
        </div>
        <Radio
          label='True'
          checked={hasRedemptions === HasRedemptions.TRUE}
          id='true'
          onChange={() => setHasRedemptions(HasRedemptions.TRUE)}
        />
        <Radio
          label='False'
          checked={hasRedemptions === HasRedemptions.FALSE}
          id='false'
          onChange={() => setHasRedemptions(HasRedemptions.FALSE)}
        />
        <div className='input-explanation'>
          <p>Select &quot;True&quot; if you track code usage.</p>
        </div>
        <DmaRadioGroup hasDma={hasDma} setHasDma={setHasDma} />
      </div>
    )}
    {!isPaid && (
      <div className='has-redemptions-input'>
        <DmaRadioGroup hasDma={hasDma} setHasDma={setHasDma} />
      </div>
    )}
    <div className='time-frame-input'>
      <div className='section-title'>
        Time Frame<span className='asterisk'>*</span>
      </div>
      <DateInputField
        label='Start Date'
        value={startDateStr}
        setValue={setStartDateStr}
        customValidation={[
          {
            validate: value => {
              const parsedDate = new Date(value);

              return isBefore(parsedDate, startOfToday());
            },
            errorMessage: 'Start date must be in the past.'
          }
        ]}
        infoMessage='mm/dd/yyyy'
      />
      <div className='input-explanation date-input-explanation'>
        <p>
          Important: Spend should be entered daily whenever possible. If your data has been
          aggregated, please disaggregate it to the daily level.
        </p>
        <p>However, if you only spend on specific days, please do not disaggregate.</p>
      </div>
    </div>
  </>
);

interface AddMediaInputModalProps {
  show: boolean;
  handleClose: () => void;
  createTable: (input: {
    channelId: number;
    platformId: number;
    segmentMap: Record<string, number>;
    hasImpressions: boolean;
    hasRedemptions: boolean;
    hasDma: boolean;
    isPaid: boolean;
    otherUnitLabel?: string;
    additionalDescription?: string;
    startDate: Date;
  }) => void;
  isPaid: boolean;
  customSegments?: CustomSegmentsData[];
  mode?: MediaInputModalModeType;
  existingTable?: MediaTable;
  editTable?: (input: {
    tableId: string;
    segmentMap: Record<string, number>;
    additionalDescription?: string;
    hasDma?: boolean;
  }) => void;
}

export const AddMediaInputModal = ({
  show,
  handleClose,
  createTable,
  isPaid,
  customSegments,
  mode = MediaInputModalMode.CREATE,
  existingTable,
  editTable
}: AddMediaInputModalProps) => {
  const isEditMode = mode === MediaInputModalMode.EDIT;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [segmentMap, setSegmentMap] = useState<Record<string, number>>({});
  const [hasImpressions, setHasImpressions] = useState(false);
  const [otherUnitChecked, setOtherUnitChecked] = useState(false);
  const [otherUnitLabel, setOtherUnitLabel] = useState('');
  const [showSpendWarning, setShowSpendWarning] = useState(false);
  const [hasRedemptions, setHasRedemptions] = useState<HasRedemptionsType>(
    HasRedemptions.UNSELECTED
  );
  const [startDateStr, setStartDateStr] = useState('');
  const [additionalDescription, setAdditionalDescription] = useState('');
  const [hasDma, setHasDma] = useState(false);

  const resetState = () => {
    setSegmentMap({});
    setHasImpressions(false);
    setOtherUnitChecked(false);
    setOtherUnitLabel('');
    setShowSpendWarning(false);
    setHasRedemptions(HasRedemptions.UNSELECTED);
    setStartDateStr('');
    setAdditionalDescription('');
    setHasDma(false);
    setIsSubmitting(false);
  };

  // When opening in edit mode, seed the segment map from the existing table.
  // Channel and Platform are top-level fields on MediaTable, so include them
  // explicitly in case the stored segmentMap omits them. Other fields are
  // intentionally not seeded since their sections are hidden.
  useEffect(() => {
    if (show && isEditMode && existingTable) {
      setSegmentMap({
        ...(existingTable.segmentMap ?? {}),
        [RequiredSegmentIds.CHANNEL]: existingTable.channelId,
        [RequiredSegmentIds.PLATFORM]: existingTable.platformId
      });
      setAdditionalDescription(existingTable.additionalDescription ?? '');
      setHasDma(existingTable.hasDma ?? false);
    }
  }, [show, isEditMode, existingTable]);

  const requiredSegments = useMemo(() => ['Channel', 'Platform'], []);

  const hasAllRequiredSegments = useMemo(() => {
    if (!customSegments) {
      return false;
    }
    return requiredSegments.every(segmentName => {
      const segment = customSegments.find(seg => seg.segmentName === segmentName);
      return segment && segmentMap[segment.segmentId] !== undefined;
    });
  }, [customSegments, requiredSegments, segmentMap]);

  const submitDisabled =
    isSubmitting ||
    !hasAllRequiredSegments ||
    (!isEditMode &&
      ((otherUnitChecked && !otherUnitLabel) ||
        (isPaid && hasRedemptions === HasRedemptions.UNSELECTED) ||
        (!isPaid && !hasImpressions && !otherUnitChecked) ||
        !startDateStr));

  const handleSegmentChange = useCallback((segmentId: string, valueId: number | null) => {
    if (valueId !== null) {
      setSegmentMap(prev => ({
        ...prev,
        [segmentId]: valueId
      }));
    } else {
      setSegmentMap(prev => {
        const newMap = { ...prev };
        delete newMap[segmentId];
        return newMap;
      });
    }
  }, []);

  const segmentDropdowns = useMemo(() => {
    if (!customSegments) {
      return null;
    }

    // Sort segments so that required ones are first, then alphabetically
    const segmentOrder: Record<string, number> = { Channel: 0, Platform: 1 };
    const sortedSegments = [...customSegments].sort((a, b) => {
      const orderA = segmentOrder[a.segmentName] ?? Infinity;
      const orderB = segmentOrder[b.segmentName] ?? Infinity;
      return orderA !== orderB ? orderA - orderB : a.segmentName.localeCompare(b.segmentName);
    });

    return sortedSegments.map(segment => {
      const isRequired = requiredSegments.includes(segment.segmentName);
      // In edit mode Channel and Platform are locked; other custom segments remain editable.
      const isLocked = isEditMode && isRequired;
      const options = segment.values
        .map(value => value.valueName)
        .sort((a, b) => a.localeCompare(b));

      const selectedValueId = segmentMap[segment.segmentId];
      const selectedValueData = segment.values.find(v => v.valueId === selectedValueId);
      const selectedValue = selectedValueData?.valueName || '';

      return (
        <div key={segment.segmentId} className='segment-input'>
          <div className='section-title'>
            {segment.segmentName}
            {isRequired && !isEditMode && <span className='asterisk'>*</span>}
          </div>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            placeholder={`Select ${segment.segmentName}`}
            value={selectedValue}
            onChange={value => {
              const matchedValue = segment.values.find(v => v.valueName === value);
              handleSegmentChange(segment.segmentId, matchedValue ? matchedValue.valueId : null);
            }}
            options={options}
            clearable={!isRequired && !isLocked}
            onClear={() => handleSegmentChange(segment.segmentId, null)}
            disabled={isLocked}
          />
        </div>
      );
    });
  }, [customSegments, segmentMap, requiredSegments, handleSegmentChange, isEditMode]);

  return (
    <Modal
      className={cn('add-input-modal', 'add-media-input-modal', {
        'edit-media-input-modal': isEditMode
      })}
      show={show}
      onHide={handleClose}
      size={isEditMode ? 'lg' : 'xl'}
      centered
    >
      <Modal.Header>
        <h4 className='modal-title'>
          {isEditMode ? 'Edit' : 'Add'} Manual {isPaid ? 'Paid' : 'Non-Paid'} Media Input
        </h4>
        {!isEditMode && <div className='sub-title'>*Required fields marked with an asterisk.</div>}
      </Modal.Header>
      <Modal.Body>
        <div className='custom-segments-section'>
          {segmentDropdowns}
          <div className='segment-input additional-description-input'>
            <div className='section-title'>Additional Description</div>
            <Form.Control
              type='text'
              value={additionalDescription}
              onChange={e => setAdditionalDescription(e.target.value)}
            />
          </div>
        </div>
        {isEditMode && (
          <div className='has-dma-input'>
            <DmaRadioGroup hasDma={hasDma} setHasDma={setHasDma} />
          </div>
        )}
        {!isEditMode && (
          <CreateModeFields
            isPaid={isPaid}
            hasImpressions={hasImpressions}
            setHasImpressions={setHasImpressions}
            otherUnitChecked={otherUnitChecked}
            setOtherUnitChecked={setOtherUnitChecked}
            otherUnitLabel={otherUnitLabel}
            setOtherUnitLabel={setOtherUnitLabel}
            showSpendWarning={showSpendWarning}
            setShowSpendWarning={setShowSpendWarning}
            hasRedemptions={hasRedemptions}
            setHasRedemptions={setHasRedemptions}
            hasDma={hasDma}
            setHasDma={setHasDma}
            startDateStr={startDateStr}
            setStartDateStr={setStartDateStr}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={() => {
            handleClose();
            resetState();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            setIsSubmitting(true);
            if (isEditMode && existingTable && editTable) {
              editTable({
                tableId: existingTable.tableId,
                segmentMap,
                additionalDescription: additionalDescription.trim() || undefined,
                hasDma
              });
            } else {
              await createTable({
                channelId: segmentMap[RequiredSegmentIds.CHANNEL],
                platformId: segmentMap[RequiredSegmentIds.PLATFORM],
                segmentMap,
                hasImpressions,
                hasRedemptions: hasRedemptions === HasRedemptions.TRUE,
                hasDma,
                isPaid,
                otherUnitLabel,
                additionalDescription: additionalDescription.trim() || undefined,
                startDate: new Date(startDateStr)
              });
            }
            handleClose();
            resetState();
          }}
          disabled={submitDisabled}
          loading={isSubmitting}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
